import { Component, OnInit, OnDestroy, inject, signal, effect, Injector } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { ProjectService } from '../../services/project.service';
import { ProjectContextService } from '../../services/project-context.service';
import { Project } from '../../dtos/project';
import {
  ProjectState,
  formatStageState,
  getStageStateClass,
  stageForChildPath,
  stageToChildRoute,
} from '../../dtos/project-state';
import { ProjectStageNavComponent } from './project-stage-nav/project-stage-nav.component';
import { ProjectEditModalComponent } from '../modals/project-edit-modal/project-edit-modal.component';
import { ConfirmModalComponent } from '../modals/confirm-modal/confirm-modal.component';
import { GroupHoverPopupComponent } from '../modals/group-hover-popup/group-hover-popup.component';
import { ProjectExtractionQuestionsComponent } from './project-extraction-questions/project-extraction-questions.component';
import { ProjectGuideComponent } from './project-guide/project-guide.component';
import { AuditTrailComponent } from './audit-trail/audit-trail.component';

@Component({
  selector: 'app-project-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    ProjectStageNavComponent,
    ProjectEditModalComponent,
    ConfirmModalComponent,
    GroupHoverPopupComponent,
    ProjectExtractionQuestionsComponent,
    ProjectGuideComponent,
    AuditTrailComponent,
  ],
  templateUrl: './project-layout.component.html',
})
export class ProjectLayoutComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private projectService = inject(ProjectService);
  readonly ctx = inject(ProjectContextService);
  private injector = inject(Injector);

  readonly currentStage = signal<ProjectState>(ProjectState.SETUP);
  readonly showEditModal = signal(false);
  readonly showGuide = signal(false);
  readonly showAuditTrail = signal(false);
  readonly auditTrailPaperId = signal<number | null>(null);
  readonly guideAnchor = signal<string | undefined>(undefined);
  readonly descriptionExpanded = signal(false);

  projectId = '';
  private sub?: Subscription;

  showLeaveConfirm = false;
  leaveLoading = false;
  leaveError = '';

  ngOnInit(): void {
    this.sub = this.route.params.subscribe((params) => {
      this.projectId = params['projectId'];
      this.refreshProjectContext();
    });
    this.ctx.loadUserInfo();

    this.syncStage();
    this.sub.add(
      this.projectService.projectUpdated$.subscribe(() => {
        this.ctx.loadProject(this.projectId);
      }),
    );
    this.sub.add(
      this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
        this.syncStage();
        this.refreshProjectContext();
        this.redirectToStageIfOnRoot();
      }),
    );

    const redirectOnLoad = effect(
      () => {
        const project = this.ctx.project();
        if (project && project.id === this.projectId && this.projectId) {
          this.redirectToStageIfOnRoot();
        }
      },
      { injector: this.injector },
    );
    this.sub.add({ unsubscribe: () => redirectOnLoad.destroy() });

    const watchGuide = effect(
      () => {
        const anchor = this.ctx.guideAnchorRequest();
        if (anchor) {
          this.guideAnchor.set(anchor);
          this.showGuide.set(true);
          this.ctx.guideAnchorRequest.set(null);
        }
      },
      { injector: this.injector },
    );
    this.sub.add({ unsubscribe: () => watchGuide.destroy() });

    const watchAuditTrail = effect(
      () => {
        const paperId = this.ctx.auditTrailPaperId();
        if (paperId !== null) {
          this.auditTrailPaperId.set(paperId);
          this.showAuditTrail.set(true);
          this.ctx.auditTrailPaperId.set(null);
        }
      },
      { injector: this.injector },
    );
    this.sub.add({ unsubscribe: () => watchAuditTrail.destroy() });
  }

  private syncStage(): void {
    const segments = this.route.snapshot.firstChild?.url.map((s) => s.path) ?? [];
    this.currentStage.set(stageForChildPath(segments));
  }

  private refreshProjectContext(): void {
    if (this.projectId) {
      this.ctx.loadProject(this.projectId);
    }
  }

  private redirectToStageIfOnRoot(): void {
    const proj = this.ctx.project();
    if (!proj) return;
    const child = this.route.snapshot.firstChild;
    const onRootPage = !child?.url.length || !child.url[0].path;
    if (onRootPage) {
      const route = stageToChildRoute(proj.state);
      if (route) {
        this.router.navigate(['/projects', this.projectId, route], { replaceUrl: true });
      }
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get project() {
    return this.ctx.project;
  }

  get group() {
    return this.ctx.group;
  }

  doGoBack(): void {
    this.router.navigate(['/projects']);
  }

  isProjectPI(): boolean {
    return this.ctx.isProjectPI();
  }

  navigateToAnnotator(): void {
    this.router.navigate(['/projects', this.projectId, 'annotator']);
  }

  showAnnotatorButton(_p: Project): boolean {
    return false;
  }

  get leaveMessage(): string {
    const title = this.project()?.title ?? '';
    return `Are you sure you want to leave the project "${title}"?`;
  }

  isProjectResearcher(): boolean {
    const p = this.project();
    return !!p && p.userRole === 'RESEARCHER';
  }

  onLeaveProject(): void {
    this.leaveError = '';
    this.showLeaveConfirm = true;
  }

  confirmLeaveProject(): void {
    const p = this.project();
    if (!p || this.leaveLoading) return;

    this.leaveLoading = true;
    this.leaveError = '';

    this.projectService.leaveProject(p.id).subscribe({
      next: () => {
        this.showLeaveConfirm = false;
        this.leaveLoading = false;
        this.router.navigate(['/projects']);
      },
      error: (err) => {
        this.leaveError =
          err?.error?.message ??
          err?.error?.detail ??
          err?.message ??
          'An error occurred while leaving the project.';
        this.showLeaveConfirm = false;
        this.leaveLoading = false;
      },
    });
  }

  cancelLeaveProject(): void {
    this.showLeaveConfirm = false;
    this.leaveLoading = false;
    this.leaveError = '';
  }

  onProjectDeleted(): void {
    this.router.navigate(['/projects']);
  }

  onAuditTrailChange(open: boolean): void {
    this.showAuditTrail.set(open);
    if (!open) {
      this.auditTrailPaperId.set(null);
    }
  }

  onProjectUpdated(updated: Project): void {
    this.ctx.project.set(updated);
    this.showEditModal.set(false);
    this.projectService.notifyProjectUpdated();
  }

  openGuide(): void {
    this.guideAnchor.set(undefined);
    this.showGuide.set(true);
  }

  closeGuide(): void {
    this.showGuide.set(false);
  }

  getStageStateClass(state?: string): string {
    return getStageStateClass(state);
  }

  formatStageState(state?: string): string {
    if (state === 'SETUP' || state === 'INGESTION') return 'Setup / Ingestion';
    return formatStageState(state);
  }
}
